import { Component, OnInit, Input, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { ColDef, ColumnApi, GridApi, GridReadyEvent, ITooltipParams, ValueGetterParams } from 'ag-grid-community';
import { ColumnMapping } from 'src/app/model/result-grid/column-mapping';
import { LinkCellRenderer } from '../link-cell-renderer/link-cell-renderer.component';
import { DatePipe } from '@angular/common';
import { catchError, Observable } from 'rxjs';
import { ActionLinkCellRenderer as ActionLinkCellRenderer } from '../action-link-cell-renderer/action-link-cell-renderer.component';
import { CheckboxCellRendererComponent as CheckboxCellRenderer } from '../checkbox-cell-renderer/checkbox-cell-renderer.component';
import { MultiActionLinkCellRendererComponent } from '../multi-action-link-cell-renderer/multi-action-link-cell-renderer.component';

@Component({
  selector: 'result-grid',
  templateUrl: './result-grid.component.html',
  styleUrls: ['./result-grid.component.scss']
})
export class ResultGridComponent implements OnInit, OnChanges {

  @Input() columns!: ColumnMapping[];
  @Input() callback!: (args: any) => Observable<any>;
  @Input() export: boolean = false;
  @Input() hasAction: boolean = false;
  @Input() showSearchContent: boolean = true;
  @Input() customTitle: string = "";
  @Input() instructionMessage?: string;
  @Output() isProcessing = new EventEmitter<boolean>();
  @Input() pagination: boolean = true;
  @Input() userJourneyName!: string;
  @Input() isModelGridViewWithSize: 'small' | 'medium' | 'large' | number | false = false;
  @Input() autoFitEnable: boolean = false;
  @Input() exportType = [{ value: "Export" }];
  @Output() isRowSelected = new EventEmitter<boolean>();
  @Output() additionalData = new EventEmitter<any>();

  constructor(private dateFormatter: DatePipe) { }

  private gridApi!: GridApi;
  private gridColumnApi!: ColumnApi;
  private searchCriteria!: any;
  private rowSelectionSet: boolean = false;

  public columnDefinitions!: ColDef[];
  public rowData: any[] = [];
  public defaultColumnDefinitions: ColDef = {
    resizable: true,
    sortable: true
  };
  public pageSize: number = 100;
  public totalRecords: number = 0;
  public currentPageIndex: number = 1;
  public tooltipShowDelay: number = 500;
  public rowSelection!: 'single' | 'multiple';
  public suppressRowClilckSelection!: boolean;
  public context: any = {};
  public noRowsTemplate!: string;
  public loadingTemplate = `<span class="ag-overlay-loading-center ag-custom-overlay">Records are loading... </span>`;

  public onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  public moveToPage($event: any) {
    if ($event.detail?.toPageNumber > 0) {
      this.searchCriteria.pageNumber = $event.detail?.toPageNumber;
      this.currentPageIndex = $event.detail?.toPageNumber;
      this.fetch(this.searchCriteria);
    }
  }

  ngOnInit(): void {
    this.createColumnDefinitions();
    let message = "No data to show."
    if (this.showSearchContent) {
      message = message + " Use search above to get results";
    }
    this.noRowsTemplate = '<span class="ag-custom-overlay no-data-found">' + message + '</span>';
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const property in changes) {
      if (changes.hasOwnProperty(property) && !changes[property].firstChange) {
        if (property === "columns") {
          this.createColumnDefinitions();
        }
      }
    }
  }

  public fetch(searchCriteria: any): void {
    this.isProcessing.emit(true);
    this.gridApi.showLoadingOverlay();
    this.searchCriteria = searchCriteria;
    if (this.searchCriteria.pageNumber == 1) {
      this.currentPageIndex = 1;
    }

    this.callback(searchCriteria).pipe(
      catchError((err) => {
        this.gridApi.hideOverlay();
        // For placeholder to exist on grid in cases of error
        this.gridApi.setRowData(this.rowData);
        this.isProcessing.emit(false);
        return err;
      })
    ).subscribe((response: any) => {
      if (response.data !== null && response.data.data instanceof Array && response.data.data.length > 0) {
        this.rowData = <Array<any>><unknown>response.data.data;
        this.totalRecords = this.context.totalRecords = response.data.totalCount;
      } else {
        this.clearGrid();
      }
      this.detemineGridHeight();
      this.gridApi.setRowData(this.rowData);
      if (this.autoFitEnable && typeof window !== 'undefined') {
        (<any>window).sizeToFit = this.gridApi.sizeColumnsToFit();
      }
      this.gridApi.hideOverlay();
      this.isProcessing.emit(false);
      this.additionalData.emit({totalRecords : this.totalRecords, data : response?.data?.additionalData});
    });
  }
  public clearGrid(): void {
    this.rowData = [];
    this.totalRecords = this.context.totalRecords = 0;
  }

  public getSelectedRows(): any[] {
    return this.gridApi.getSelectedRows();
  }

  public getAllRows(): any[] {
    return this.rowData;
  }

  public determineGridContainerPadding(): string {
    if (typeof this.isModelGridViewWithSize === "boolean") {
      return '0px 15px';
    }
    return '0px 0px'
  }

  public detemineGridHeight(): string {
    let height: string = '400px';
    if (typeof this.isModelGridViewWithSize !== "boolean") {
      if (this.rowData.length <= 1) {
        height = '108px';
      }
      else if (this.rowData.length == 2) {
        height = '160px';
      }
      else if (this.isModelGridViewWithSize == 'small'
        || ((this.isModelGridViewWithSize == 'medium' || this.isModelGridViewWithSize == 'large') && this.rowData.length == 3)
        || (typeof this.isModelGridViewWithSize === "number" && this.isModelGridViewWithSize > 200 && this.isModelGridViewWithSize <= 320 && this.rowData.length == 3)) {
        height = '200px';
      }
      else if (this.isModelGridViewWithSize == 'medium'
        || (this.isModelGridViewWithSize == 'large' && this.rowData.length <= 6)
        || (typeof this.isModelGridViewWithSize === "number" && this.isModelGridViewWithSize > 320 && this.isModelGridViewWithSize <= 400 && this.rowData.length <= 6)) {
        height = '320px';
      }
      else if (typeof this.isModelGridViewWithSize === "number") {
        height = this.isModelGridViewWithSize + 'px';
      }
    }
    return height;
  }

  private createColumnDefinitions(): void {
    const columnDef: ColDef[] = []
    this.rowSelectionSet = false;
    this.rowSelection = 'single';
    this.suppressRowClilckSelection = false;

    this.columns.forEach((column) => {
      let colDefinition: ColDef = {};
      colDefinition.headerName = column.columnHeaderName;
      colDefinition.cellStyle = column.cellStyle;
      this.configureColumnValueField(column, colDefinition);
      if (column.dateColumn != undefined) {
        this.configureDateColumn(column, colDefinition);
      }
      else if (column.isAmount) {
        colDefinition.cellClass = 'ag-right-aligned-cell';
      }
      else if (column.checkboxColumn != undefined) {
        this.configureCheckboxColumn(colDefinition, column);
      }
      else if (column.columnLink != undefined) {
        this.configureColumnLink(colDefinition, column);
      }
      else if (column.actionLink != undefined) {
        this.configureActionLink(colDefinition, column);
      }
      else if (column.multiActionLink != undefined) {
        this.configureMultiActionLink(colDefinition, column);
      }
      if (column.enableTooltip) {
        colDefinition.tooltipValueGetter = (params) => this.getToolTipValue(params);
      }
      if (column.width) {
        colDefinition.width = column.width;
      }
      this.configureRowSelection(colDefinition, column);
      columnDef.push(colDefinition);
    })
    this.columnDefinitions = columnDef;
  }

  private configureCheckboxColumn(colDefinition: ColDef<any>, column: ColumnMapping) {
    colDefinition.cellRenderer = CheckboxCellRenderer;
    colDefinition.cellRendererParams = {
      isEditable: column.checkboxColumn?.isEditable
    };
    colDefinition.cellClass = ['checkbox-cell-renderer', 'dxp-checkbox input[type=checkbox]+label'];
  }

  private configureRowSelection(colDefinition: ColDef<any>, column: ColumnMapping) {
    if (column.rowSelectionForColumn != undefined && !this.rowSelectionSet) {
      colDefinition.headerCheckboxSelection = column.rowSelectionForColumn.headerCheckboxSelection;
      colDefinition.checkboxSelection = column.rowSelectionForColumn.enableRowSelectionForColumn;
      this.rowSelection = column.rowSelectionForColumn.rowSelection;
      this.suppressRowClilckSelection = true;
      this.rowSelectionSet = true;
    }
  }

  private configureColumnValueField(column: ColumnMapping, colDefinition: ColDef<any>): void {
    if (column.valueAggregator === undefined) {
      colDefinition.field = column.columnName;
    }
    else {
      colDefinition.valueGetter = (param) => this.getValueAggregatorOutput(param, column);
    }
  }

  private configureDateColumn(column: ColumnMapping, colDefinition: ColDef<any>): void {
    let dateFormat: string;
    if (column.dateColumn?.CustomDateFormat != ('' || undefined)) {
      dateFormat = column.dateColumn.CustomDateFormat;
    }
    else if (column.dateColumn?.DateWithTime) {
      dateFormat = 'dd MMM yyyy h:mm aaa';
    }
    else {
      dateFormat = 'dd MMM yyyy';
    }
    colDefinition.valueFormatter = (params): any => {
      let value = this.dateFormatter.transform(params.value, dateFormat, undefined, 'en-US');
      return value;
    };
  }

  private configureColumnLink(colDefinition: ColDef<any>, column: ColumnMapping): void {
    colDefinition.cellRenderer = LinkCellRenderer;
    colDefinition.cellRendererParams = {
      linkData: column.columnLink
    };
    this.setCommonLinkProperties(colDefinition, column.actionLink);
  }

  private configureActionLink(colDefinition: ColDef<any>, column: ColumnMapping): void {
    if (colDefinition.headerName === '') {
      colDefinition.headerName = 'Action';
    }
    if (column.actionLink?.DisplayText === undefined) {
      colDefinition.field = column.columnName;
    }
    colDefinition.cellRenderer = ActionLinkCellRenderer;
    colDefinition.cellRendererParams = {
      linkData: column.actionLink
    };
    this.setCommonLinkProperties(colDefinition, column.actionLink);
  }

  private configureMultiActionLink(colDefinition: ColDef<any>, column: ColumnMapping): void {
    if (colDefinition.headerName === '') {
      colDefinition.headerName = 'Actions';
    }
    colDefinition.cellRenderer = MultiActionLinkCellRendererComponent;
    colDefinition.cellRendererParams = {
      linkData: column.multiActionLink
    };
    this.setCommonLinkProperties(colDefinition, column.actionLink);
  }

  private getValueAggregatorOutput(params: ValueGetterParams<any>, column: ColumnMapping): any {
    let value = column.valueAggregator?.(params.data);
    return value;
  }

  private getToolTipValue(params: ITooltipParams<any, any>): any {
    return (params.valueFormatted ? params.valueFormatted : params.value);
  }

  private setCommonLinkProperties(colDefinition: ColDef<any>, linkReference: any): void {
    colDefinition.cellClass = 'ag-center-aligned-cell';
    colDefinition.headerClass = ['ag-center-aligned-cell'];

    if (linkReference?.isPinned === undefined || linkReference?.isPinned !== false) {
      colDefinition.pinned = 'left';
      colDefinition.headerClass.push('ag-pinned-header-cell-label');
    }
  }

  onRowSelected(){
    this.isRowSelected.emit(true);
  }
}